/**
 * 멍투데이 시스템 공지사항 - 비즈니스앱용
 *
 * 백엔드 API 명세서 §2 기반. 토큰의 userType=BUSINESS/TEACHER 사용자 전용.
 * 서버에서 targetApp=BUSINESS 공지만 조회됨 (클라이언트가 별도로 보내지 않음).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";

// ─────────────────────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────────────────────

export type AnnouncementCategory =
  | "SYSTEM"
  | "MAINTENANCE"
  | "EVENT"
  | "GENERAL";

export interface AnnouncementItem {
  id: number;
  category: AnnouncementCategory;
  title: string;
  contentHtml: string;
  isImportant: boolean;
  isPinned: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  createdAt: string;
}

export interface AnnouncementListItem extends AnnouncementItem {
  isRead: boolean;
}

interface ServiceResult<T> {
  codeEnum: { value: string; desc: string; status: number };
  data: T;
}

// ─────────────────────────────────────────────────────────────
// React Query 키
// ─────────────────────────────────────────────────────────────

export const businessAnnouncementKeys = {
  list: ["business-announcement-list"] as const,
  detail: (id: number | undefined) =>
    ["business-announcement-detail", id] as const,
  unread: ["business-announcement-unread"] as const,
};

// ─────────────────────────────────────────────────────────────
// API 호출
// ─────────────────────────────────────────────────────────────

async function fetchList(): Promise<AnnouncementListItem[]> {
  const { data } = await privateHttp.get<ServiceResult<AnnouncementListItem[]>>(
    API_ENDPOINTS.BUSINESS_ANNOUNCEMENT.LIST,
  );
  return data.data;
}

async function fetchDetail(id: number): Promise<AnnouncementListItem> {
  const { data } = await privateHttp.get<ServiceResult<AnnouncementListItem>>(
    `${API_ENDPOINTS.BUSINESS_ANNOUNCEMENT.DETAIL}/${id}`,
  );
  return data.data;
}

async function fetchUnreadCount(): Promise<{ count: number }> {
  const { data } = await privateHttp.get<ServiceResult<{ count: number }>>(
    API_ENDPOINTS.BUSINESS_ANNOUNCEMENT.UNREAD_COUNT,
  );
  return data.data;
}

async function postMarkAsRead(id: number): Promise<void> {
  await privateHttp.post(`${API_ENDPOINTS.BUSINESS_ANNOUNCEMENT.READ}/${id}/read`);
}

// ─────────────────────────────────────────────────────────────
// React Query hooks
// ─────────────────────────────────────────────────────────────

export const useBusinessAnnouncementList = () => {
  return useQuery({
    queryKey: businessAnnouncementKeys.list,
    queryFn: fetchList,
    staleTime: 30 * 1000,
  });
};

export const useBusinessAnnouncementDetail = (id: number | undefined) => {
  return useQuery({
    queryKey: businessAnnouncementKeys.detail(id),
    queryFn: () =>
      id == null
        ? Promise.resolve(null as AnnouncementListItem | null)
        : fetchDetail(id),
    enabled: id != null && !Number.isNaN(id),
  });
};

export const useBusinessAnnouncementUnreadCount = () => {
  return useQuery({
    queryKey: businessAnnouncementKeys.unread,
    queryFn: fetchUnreadCount,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useBusinessAnnouncementMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postMarkAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessAnnouncementKeys.list,
      });
      queryClient.invalidateQueries({
        queryKey: businessAnnouncementKeys.unread,
      });
    },
  });
};
